import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  Filter,
  Calendar,
  MoreVertical,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import customerService from '@/services/customerService';
import { cn } from '@/lib/utils';

/**
 * Müşteri durum badge bileşeni
 */
function CustomerStatusBadge({ status }) {
  const variants = {
    new: { label: 'Yeni', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    contacted: { label: 'İletişime Geçildi', className: 'bg-orange-100 text-orange-700 border-orange-200' },
    interested: { label: 'İlgileniyor', className: 'bg-green-100 text-green-700 border-green-200' },
    not_interested: { label: 'İlgilenmiyor', className: 'bg-gray-100 text-gray-700 border-gray-200' },
    closed: { label: 'Kapatıldı', className: 'bg-red-100 text-red-700 border-red-200' },
  };
  const config = variants[status] || variants.new;
  return <Badge className={cn('border', config.className)}>{config.label}</Badge>;
}

/**
 * Lead kaynağı badge bileşeni
 */
function LeadSourceBadge({ source }) {
  if (!source) return <span className="text-muted-foreground">-</span>;
  
  const variants = {
    web: { label: 'Web', className: 'bg-purple-100 text-purple-700 border-purple-200' },
    phone: { label: 'Telefon', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    social_media: { label: 'Sosyal Medya', className: 'bg-pink-100 text-pink-700 border-pink-200' },
    referral: { label: 'Referans', className: 'bg-green-100 text-green-700 border-green-200' },
    office_visit: { label: 'Ofis Ziyareti', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    other: { label: 'Diğer', className: 'bg-gray-100 text-gray-700 border-gray-200' },
  };
  const config = variants[source] || variants.other;
  return <Badge variant="outline" className={cn('border', config.className)}>{config.label}</Badge>;
}

/**
 * Tarih formatlama
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Tarih kontrolü - Gecikmiş mi?
 */
function isOverdue(dateString) {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Tablo skeleton loader
 */
function TableSkeleton() {
  return (
    <>
      {[...Array(10)].map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-5 w-5" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-32" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-40" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-28" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-24" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <div className="flex gap-2">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-8" />
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * StaffCustomersPage - Müşteri yönetimi listesi sayfası
 */
const StaffCustomersPage = () => {
  const { toast } = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  
  // Filtreler
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [leadSourceFilter, setLeadSourceFilter] = useState('all');
  const [followUpFilter, setFollowUpFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Seçili müşteriler
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  // Sayfalama
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const { data, error } = await customerService.getCustomers({
        limit: 1000, // Tüm müşterileri al, filtreleme client-side yapılacak
      });
      
      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Müşteriler yüklenirken bir hata oluştu.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Filtrelenmiş ve sıralanmış müşteriler
  const filteredCustomers = useMemo(() => {
    let filtered = [...customers];

    // Arama filtresi
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.first_name?.toLowerCase().includes(query) ||
          c.last_name?.toLowerCase().includes(query) ||
          c.email?.toLowerCase().includes(query) ||
          c.phone?.includes(query)
      );
    }

    // Durum filtresi
    if (statusFilter !== 'all') {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }

    // Lead kaynağı filtresi
    if (leadSourceFilter !== 'all') {
      filtered = filtered.filter((c) => c.lead_source === leadSourceFilter);
    }

    // Follow-up filtresi
    if (followUpFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();
      const tomorrowISO = new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString();
      const weekLaterISO = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();

      if (followUpFilter === 'today') {
        filtered = filtered.filter(
          (c) => c.next_follow_up && c.next_follow_up >= todayISO && c.next_follow_up < tomorrowISO
        );
      } else if (followUpFilter === 'this_week') {
        filtered = filtered.filter(
          (c) => c.next_follow_up && c.next_follow_up >= todayISO && c.next_follow_up < weekLaterISO
        );
      } else if (followUpFilter === 'overdue') {
        filtered = filtered.filter((c) => c.next_follow_up && isOverdue(c.next_follow_up));
      } else if (followUpFilter === 'no_schedule') {
        filtered = filtered.filter((c) => !c.next_follow_up);
      }
    }

    // Sıralama
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'last_contact':
          if (!a.last_contacted_at && !b.last_contacted_at) return 0;
          if (!a.last_contacted_at) return 1;
          if (!b.last_contacted_at) return -1;
          return new Date(b.last_contacted_at) - new Date(a.last_contacted_at);
        case 'name_asc':
          const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
          const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
          return nameA.localeCompare(nameB);
        default:
          return 0;
      }
    });

    return filtered;
  }, [customers, searchQuery, statusFilter, leadSourceFilter, followUpFilter, sortBy]);

  // Sayfalama
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Tümünü seç/bırak
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCustomers(paginatedCustomers.map((c) => c.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  // Tekil seçim
  const handleSelectCustomer = (customerId, checked) => {
    if (checked) {
      setSelectedCustomers((prev) => [...prev, customerId]);
    } else {
      setSelectedCustomers((prev) => prev.filter((id) => id !== customerId));
    }
  };

  // Toplu işlemler
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedCustomers.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Uyarı',
        description: 'Lütfen en az bir müşteri seçin.',
      });
      return;
    }

    try {
      const { error } = await customerService.bulkUpdateStatus(selectedCustomers, newStatus);
      if (error) throw error;

      toast({
        title: 'Başarılı',
        description: `${selectedCustomers.length} müşterinin durumu güncellendi.`,
      });
      setSelectedCustomers([]);
      fetchCustomers();
    } catch (error) {
      console.error('Error updating bulk status:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Durum güncellenirken bir hata oluştu.',
      });
    }
  };

  const handleBulkFollowUpAssign = async () => {
    if (selectedCustomers.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Uyarı',
        description: 'Lütfen en az bir müşteri seçin.',
      });
      return;
    }

    // Basit bir prompt ile tarih al (daha sonra DatePicker eklenebilir)
    const dateStr = prompt('Follow-up tarihi (YYYY-MM-DD):');
    if (!dateStr) return;

    try {
      const followUpDate = new Date(dateStr).toISOString();
      const { error } = await customerService.bulkAssignFollowUp(selectedCustomers, followUpDate);
      if (error) throw error;

      toast({
        title: 'Başarılı',
        description: `${selectedCustomers.length} müşteriye follow-up tarihi atandı.`,
      });
      setSelectedCustomers([]);
      fetchCustomers();
    } catch (error) {
      console.error('Error assigning follow-up:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Follow-up atanırken bir hata oluştu.',
      });
    }
  };

  const handleDeleteClick = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!customerToDelete) return;

    try {
      const { error } = await customerService.deleteCustomer(customerToDelete.id);
      if (error) throw error;

      toast({
        title: 'Başarılı',
        description: 'Müşteri başarıyla silindi.',
      });
      fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Müşteri silinirken bir hata oluştu.',
      });
    } finally {
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Üst Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Müşteri Yönetimi</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Toplam {filteredCustomers.length} müşteri
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Toplu İşlemler */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full sm:w-auto">
                <MoreVertical size={18} className="mr-2" />
                Toplu İşlemler
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Durum Değiştir</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleBulkStatusChange('contacted')}>
                İletişime Geçildi
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkStatusChange('interested')}>
                İlgileniyor
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkStatusChange('not_interested')}>
                İlgilenmiyor
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkStatusChange('closed')}>
                Kapatıldı
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Follow-up</DropdownMenuLabel>
              <DropdownMenuItem onClick={handleBulkFollowUpAssign}>
                <Calendar size={16} className="mr-2" />
                Follow-up Ata
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/staff/customers/new" className="w-full sm:w-auto">
            <Button className="w-full">
              <Plus size={18} className="mr-2" />
              Yeni Müşteri Ekle
            </Button>
          </Link>
        </div>
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Arama */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="İsim, e-posta veya telefon ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Durum Filtresi */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <Filter size={16} className="mr-2" />
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="new">Yeni</SelectItem>
                <SelectItem value="contacted">İletişime Geçildi</SelectItem>
                <SelectItem value="interested">İlgileniyor</SelectItem>
                <SelectItem value="not_interested">İlgilenmiyor</SelectItem>
                <SelectItem value="closed">Kapatıldı</SelectItem>
              </SelectContent>
            </Select>

            {/* Lead Kaynağı Filtresi */}
            <Select value={leadSourceFilter} onValueChange={setLeadSourceFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Lead Kaynağı" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kaynaklar</SelectItem>
                <SelectItem value="web">Web</SelectItem>
                <SelectItem value="phone">Telefon</SelectItem>
                <SelectItem value="social_media">Sosyal Medya</SelectItem>
                <SelectItem value="referral">Referans</SelectItem>
                <SelectItem value="office_visit">Ofis Ziyareti</SelectItem>
                <SelectItem value="other">Diğer</SelectItem>
              </SelectContent>
            </Select>

            {/* Follow-up Filtresi */}
            <Select value={followUpFilter} onValueChange={setFollowUpFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Follow-up" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="today">Bugün</SelectItem>
                <SelectItem value="this_week">Bu Hafta</SelectItem>
                <SelectItem value="overdue">Gecikmiş</SelectItem>
                <SelectItem value="no_schedule">Takvim Yok</SelectItem>
              </SelectContent>
            </Select>

            {/* Sıralama */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sırala" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">En Yeni</SelectItem>
                <SelectItem value="oldest">En Eski</SelectItem>
                <SelectItem value="last_contact">Son İletişim</SelectItem>
                <SelectItem value="name_asc">İsim A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Müşteri Tablosu */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        paginatedCustomers.length > 0 &&
                        paginatedCustomers.every((c) => selectedCustomers.includes(c.id))
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>E-posta</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Lead Kaynağı</TableHead>
                  <TableHead>Son İletişim</TableHead>
                  <TableHead>Sonraki Follow-up</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableSkeleton />
                ) : paginatedCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <Eye size={48} className="mb-3 opacity-20" />
                        <p className="text-lg font-medium mb-2">
                          {searchQuery || statusFilter !== 'all' || leadSourceFilter !== 'all' || followUpFilter !== 'all'
                            ? 'Sonuç bulunamadı'
                            : 'Henüz müşteri eklenmemiş'}
                        </p>
                        {!searchQuery && statusFilter === 'all' && leadSourceFilter === 'all' && followUpFilter === 'all' && (
                          <Link to="/staff/customers/new">
                            <Button variant="outline" className="mt-4">
                              <Plus size={18} className="mr-2" />
                              İlk Müşteriyi Ekle
                            </Button>
                          </Link>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCustomers.map((customer) => {
                    const overdueFollowUp = customer.next_follow_up && isOverdue(customer.next_follow_up);
                    
                    return (
                      <TableRow
                        key={customer.id}
                        className={cn(overdueFollowUp && 'bg-red-50 hover:bg-red-100')}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedCustomers.includes(customer.id)}
                            onCheckedChange={(checked) => handleSelectCustomer(customer.id, checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-foreground">
                            {customer.first_name} {customer.last_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {customer.email || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {customer.phone || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <CustomerStatusBadge status={customer.status} />
                        </TableCell>
                        <TableCell>
                          <LeadSourceBadge source={customer.lead_source} />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(customer.last_contacted_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              'text-sm',
                              overdueFollowUp ? 'text-red-600 font-semibold' : 'text-muted-foreground'
                            )}
                          >
                            {formatDate(customer.next_follow_up)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/staff/customers/${customer.id}`}>
                              <Button variant="outline" size="sm">
                                <Edit size={14} className="mr-1" />
                                Düzenle
                              </Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleDeleteClick(customer)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Sil"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Önceki
          </Button>
          <div className="flex items-center gap-1">
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <span key={page}>...</span>;
              }
              return null;
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Sonraki
          </Button>
        </div>
      )}

      {/* Silme Onay Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Müşteriyi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Müşteri ve tüm aktiviteleri kalıcı olarak silinecektir.
              <br />
              <br />
              <strong>
                {customerToDelete?.first_name} {customerToDelete?.last_name}
              </strong>{' '}
              adlı müşteriyi silmek istediğinizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffCustomersPage;
